/**
 * @file src/_modulos/prenota/tabela/layouts/filters/SearchBar.tsx
 * @description Barra de pesquisa para pré-notas com select de campo
 * 
 * Campos pesquisáveis:
 * - Todos (busca geral)
 * - Número da NF (F1_DOC)
 * - Fornecedor (A2_NOME)
 * - Usuário (F1_XUSRRA)
 */

"use client";

import { useState, useEffect, useCallback, useRef } from "react";
import { Input, SmartSelect } from "visuais";
import { IconSearch, IconX } from "@tabler/icons-react";
import { useTableStore, PRENOTAS_QUERY_KEY } from "@PN/tabela";
import { useQueryClient } from "@tanstack/react-query";
import { cn } from "visuais";

// Opções de campos para busca
const SEARCH_FIELD_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "F1_DOC", label: "Número NF" },
  { value: "A2_NOME", label: "Fornecedor" },
  { value: "F1_XUSRRA", label: "Usuário" },
];

/**
 * Componente de barra de pesquisa
 */
export function SearchBar() {
  const { searchTerm, setSearchTerm, searchField, setSearchField } = useTableStore();
  const queryClient = useQueryClient();
  const [localValue, setLocalValue] = useState(searchTerm);

  // Sincroniza com o store quando ele muda externamente
  useEffect(() => {
    setLocalValue(searchTerm);
  }, [searchTerm]);

  // Função para aplicar a busca com debounce usando useRef
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Limpa o timer ao desmontar
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  // Função para aplicar a busca com debounce
  const applySearch = useCallback(
    (value: string) => {
      // Limpa timer anterior
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }

      // Atualiza valor local imediatamente
      setLocalValue(value);

      // Aplica busca após 500ms de inatividade
      debounceTimerRef.current = setTimeout(() => {
        setSearchTerm(value);
        queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
      }, 500);
    },
    [setSearchTerm, queryClient]
  );

  // Handler para mudança do campo de busca
  const handleFieldChange = (value: string | null) => {
    const fieldValue = value || "";
    setSearchField(fieldValue);
    // Se já tem um termo de busca, reaplica a busca com o novo campo
    if (localValue) {
      queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    applySearch(value);
  };

  const handleClear = () => {
    applySearch("");
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") {
      // Aplica busca imediatamente ao pressionar Enter
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      setSearchTerm(localValue);
      queryClient.invalidateQueries({ queryKey: PRENOTAS_QUERY_KEY });
    }
  };

  const getPlaceholder = () => {
    const fieldOption = SEARCH_FIELD_OPTIONS.find(opt => opt.value === searchField);
    if (fieldOption && fieldOption.value) {
      return `Buscar por ${fieldOption.label.toLowerCase()}...`;
    }
    return "Buscar por NF, fornecedor ou usuário...";
  };

  return (
    <div className="flex items-center gap-2 min-w-0 flex-1 max-w-2xl">
      {/* Select de campo */}
      <div className="flex-shrink-0">
        <SmartSelect
          multiple={false}
          value={searchField || null}
          onChange={handleFieldChange}
          options={SEARCH_FIELD_OPTIONS}
          placeholder="Campo"
          className="w-32"
        />
      </div>

      {/* Input de busca */}
      <div className="relative flex items-center min-w-0 flex-1">
        <IconSearch className="absolute left-3 h-4 w-4 text-muted-foreground pointer-events-none z-10" />
        <Input
          type="text"
          placeholder={getPlaceholder()}
          value={localValue}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          className={cn(
            "pl-9 pr-9",
            localValue && "pr-9"
          )}
        />
        {localValue && (
          <button
            onClick={handleClear}
            className="absolute right-3 h-4 w-4 text-muted-foreground hover:text-foreground transition-colors z-10"
            type="button"
            aria-label="Limpar busca"
          >
            <IconX className="h-4 w-4" />
          </button>
        )}
      </div>
    </div>
  );
}

